import React, { useState, useEffect, useRef } from 'react';
import theme from './ProjectOrderDetails.module.scss';
import { Trans } from '@lingui/react/macro';
import { Order } from '@/types';
import { CardSection, SimpleCard, CardDropdownMenu, NoResultsCard } from '@/components/Cards';
import { DateDisplay } from '@/components/localization';
import { faEye, faDownload } from '@fortawesome/pro-light-svg-icons';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import HistoryLogTable from '@/seller/components/HistoryLog/HistoryLogTable';
import { getOpenOrderAttachmentUrl, getDownloadOrderAttachmentUrl } from '@/services/orders';
import OrderLineTab from './OrderLineTab';
import { ExternalLink, FileIcon } from '@ygtechnologies/bygr-frontend-component-library';
import { Dropdown, Tabs } from '@ygtechnologies/rubus-ui';

type OrderDetailsTabId = 'orderLine' | 'attachments' | 'orderHistory' | 'comments';

interface ProjectOrderDetailsProps {
    order: Order;
    onLoad?: () => void;
    onOrderChange: () => void;
}

const ProjectOrderDetails: React.FC<ProjectOrderDetailsProps> = ({ order, onLoad, onOrderChange }) => {
    const [activeTab, setActiveTab] = useState<OrderDetailsTabId>('orderLine');

    const loaded = useRef(false);
    useEffect(() => {
        if (!loaded.current) {
            setTimeout(() => {
                triggerHandler(onLoad);
            }, 0);

            loaded.current = true;
        }
    }, [onLoad]);

    return (
        <div className={theme.container}>
            <Tabs
                activeId={activeTab}
                onChange={(id) => setActiveTab(id as OrderDetailsTabId)}
                size="sm"
                variant="underline">
                <Tabs.Tab id="orderLine" label={<Trans>Order lines ({order.orderLines.length})</Trans>} />
                <Tabs.Tab id="attachments" label={<Trans>Attachments ({order.attachments.length})</Trans>} />
                <Tabs.Tab id="orderHistory" label={<Trans>Order History</Trans>} />
            </Tabs>
            {activeTab === 'orderLine' && <OrderLineTab onOrderChange={onOrderChange} order={order} />}
            {activeTab === 'attachments' && (
                <>
                    {order.attachments.length === 0 && (
                        <NoResultsCard variant="flat">
                            <Trans>No documents</Trans>
                        </NoResultsCard>
                    )}
                    {order.attachments.map((x) => (
                        <SimpleCard key={x.id} className={theme.orderAttachmentCard} variant="flat">
                            <ExternalLink className="link-unstyled" href={getOpenOrderAttachmentUrl(x)} target="_blank">
                                <CardSection width="52px">
                                    <FileIcon fixedWidth className={theme.fileIcon} size="3x" value={x.fileMetadata} />
                                </CardSection>
                                <CardSection caption="Name" shrink>
                                    {x.name}
                                </CardSection>
                            </ExternalLink>
                            <CardSection caption={<Trans>Created</Trans>} width="250px" right shrink>
                                <DateDisplay value={x.createdDate} />
                            </CardSection>
                            <CardSection caption={<Trans>Uploaded by</Trans>} width="250px" right shrink>
                                {x.createdByName}
                            </CardSection>
                            <CardDropdownMenu>
                                <Dropdown.Item
                                    as="a"
                                    icon={faEye}
                                    href={getOpenOrderAttachmentUrl(x)}
                                    target="_blank">
                                    <Trans>Open document</Trans>
                                </Dropdown.Item>
                                <Dropdown.Item
                                    as="a"
                                    icon={faDownload}
                                    download
                                    href={getDownloadOrderAttachmentUrl(x)}>
                                    <Trans>Download document</Trans>
                                </Dropdown.Item>
                            </CardDropdownMenu>
                        </SimpleCard>
                    ))}
                </>
            )}
            {activeTab === 'orderHistory' && (
                <HistoryLogTable
                    hiddenColumns={['objectType', 'objectName']}
                    filter={{
                        projectId: order.projectId,
                        objectIds: [order.id],
                        objectType: 'Order',
                    }}
                />
            )}
        </div>
    );
};

export default ProjectOrderDetails;
